"use client";

import { SmartImage } from "@dionatanbig2be/big2be-ui";
import { RiImageLine } from "react-icons/ri";
import { resolveMediaUrl, resolveProductImageUrl } from "@/shared/helpers/media";

interface MediaImageProps {
    /** Caminho relativo da mídia no portal (ou URL já absoluta). */
    path: string | null | undefined;
    /**
     * Qual CDN resolve o caminho: "media" (default — assets/banners do portal)
     * ou "products" (catálogo, carrinho, checkout, pedidos).
     */
    source?: "media" | "products";
    /** Arte alternativa usada até 767px; sem ela, `path` atende ambos os viewports. */
    mobilePath?: string | null;
    /** Usa a proporção intrínseca quando o backend não informa width/height. */
    intrinsic?: boolean;
    alt: string;
    width?: number | `${number}`;
    height?: number | `${number}`;
    fill?: boolean;
    priority?: boolean;
    sizes?: string;
    className?: string;
    draggable?: boolean;
    /** Aceito por compat com next/image; o SmartImage usa a qualidade default do otimizador. */
    quality?: number;
}

/**
 * Imagem servida por um dos CDNs da plataforma: resolve a URL final via
 * `resolveMediaUrl`/`resolveProductImageUrl` (conforme `source`) e delega a
 * renderização ao `SmartImage` do big2be-ui (otimizador do Next, `<picture>`
 * responsivo com `mobileSrc`, fallback).
 *
 * Sem caminho válido (ou se a imagem falhar), renderiza o placeholder cinza
 * com as mesmas dimensões — seguro para conteúdo opcional.
 */
export function MediaImage({
    path,
    source = "media",
    mobilePath,
    intrinsic = false,
    alt,
    width,
    height,
    fill,
    priority,
    sizes,
    className,
    draggable,
}: MediaImageProps) {
    const resolve = source === "products" ? resolveProductImageUrl : resolveMediaUrl;
    const src = resolve(path);
    const mobileSrc = resolve(mobilePath);
    const numericWidth = width !== undefined ? Number(width) : undefined;
    const numericHeight = height !== undefined ? Number(height) : undefined;

    const fallback = (
        <div
            role="img"
            aria-label={alt}
            className={`pointer-events-none flex select-none items-center justify-center bg-base-300 ${
                fill ? "absolute inset-0" : Number.isFinite(numericWidth) && Number.isFinite(numericHeight) ? "" : "h-full w-full"
            }`}
            style={
                !fill && Number.isFinite(numericWidth) && Number.isFinite(numericHeight)
                    ? { width: numericWidth, height: numericHeight }
                    : undefined
            }
        >
            <RiImageLine className="h-1/3 w-1/3 text-base-content/30" aria-hidden />
        </div>
    );

    return (
        <SmartImage
            src={src ?? null}
            mobileSrc={mobileSrc ?? null}
            alt={alt}
            // `intrinsic` = deixa o browser usar a proporção natural (caminho
            // "plain" do SmartImage: sem fill/priority/width/height).
            width={intrinsic ? undefined : numericWidth}
            height={intrinsic ? undefined : numericHeight}
            fill={intrinsic ? undefined : fill}
            priority={intrinsic ? undefined : priority}
            sizes={sizes}
            className={className}
            draggable={draggable}
            fallback={fallback}
        />
    );
}
